/* eslint-disable react-refresh/only-export-components --
 * Layout presets live here by convention (see CLAUDE-root.md, Grid Layout);
 * the cost is a full reload instead of HMR when this file changes in dev. */
/**
 * DynamicGrid — react-grid-layout powered free-arrange panel system.
 * Panels can be dragged, resized, added, removed, and maximised.
 * Layout is persisted in the Zustand store.
 */

import { memo, useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import GridLayout, { noCompactor, setTransform } from 'react-grid-layout';

// noCompactor has no preventCollision flag → RGL would push panels during drag.
// This variant keeps the no-op compaction but sets preventCollision = true so
// the DRAGGED item snaps back on overlap instead of pushing other panels.
const preventCollisionCompactor = { ...noCompactor, preventCollision: true };
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { useTerminalStore, selectDisplayPrice, selectFeedStatus } from '../../stores/terminal.store.js';
import { formatPriceWithSymbol, formatMarketCap } from '@arc/shared';
import type { LayoutItem } from '../../stores/terminal.store.js';
import { ChartPanel }   from '../charts/ChartPanel.js';
import { Sidebar, SidebarSettingsBadge } from '../sidebar/Sidebar.js';
import { SignalFeed }   from '../signals/SignalFeed.js';
import { ArcReactor }   from '../reactor/ArcReactor.js';
import { NewsPanel }    from '../news/NewsPanel.js';
import { TradingPanel }             from '../trading/TradingPanel.js';
import { AutoTradePanel }           from '../trading/AutoTradePanel.js';
import { PriceAlertsPanel }         from '../trading/PriceAlertsPanel.js';
import { AsterPanel }               from '../trading/AsterPanel.js';
import { MTFConfluencePanel }       from '../trading/MTFConfluencePanel.js';
import { PortfolioAnalyticsPanel }  from '../trading/PortfolioAnalyticsPanel.js';

// ─── Default layout ───────────────────────────────────────────────────────────

export const DEFAULT_LAYOUT: LayoutItem[] = [
  { i: 'sidebar',   x:  0, y: 0, w:  5, h: 10, minW: 3, maxW: 15, minH: 2 },
  { i: 'chart-0',   x:  5, y: 0, w: 13, h:  6, minW: 6, minH: 2 },
  { i: 'chart-1',   x: 18, y: 0, w: 12, h:  6, minW: 6, minH: 2 },
  { i: 'trading',   x: 30, y: 0, w:  6, h:  6, minW: 3, maxW: 15, minH: 2 },
  { i: 'signals',   x:  5, y: 6, w:  6, h:  4, minW: 3, minH: 2 },
  { i: 'autotrade', x: 11, y: 6, w: 13, h:  4, minW: 6, minH: 2 },
  { i: 'news',      x: 24, y: 6, w:  6, h:  4, minW: 3, minH: 2 },
  { i: 'reactor',   x: 30, y: 6, w:  6, h:  4, minW: 3, minH: 2 },
];

// ─── Layout presets (TradingView-style) ───────────────────────────────────────

/** 2 charts side by side, with all panels in a bottom row */
export const LAYOUT_2: LayoutItem[] = [
  { i: 'sidebar',   x:  0, y: 0, w:  5, h: 10, minW: 3, maxW: 15, minH: 2 },
  { i: 'chart-0',   x:  5, y: 0, w: 13, h:  7, minW: 6, minH: 2 },
  { i: 'chart-1',   x: 18, y: 0, w: 12, h:  7, minW: 6, minH: 2 },
  { i: 'trading',   x: 30, y: 0, w:  6, h:  7, minW: 3, maxW: 15, minH: 2 },
  { i: 'signals',   x:  5, y: 7, w:  6, h:  3, minW: 3, minH: 2 },
  { i: 'autotrade', x: 11, y: 7, w: 13, h:  3, minW: 6, minH: 2 },
  { i: 'news',      x: 24, y: 7, w:  6, h:  3, minW: 3, minH: 2 },
  { i: 'reactor',   x: 30, y: 7, w:  6, h:  3, minW: 3, minH: 2 },
];

/** 4 charts in a 2×2 grid + bottom panels row */
export const LAYOUT_4: LayoutItem[] = [
  // ── main area ────────────────────────────────────────────────────────────
  { i: 'sidebar',   x:  0, y:  0, w:  5, h: 14, minW: 3, maxW: 15, minH: 2 },
  { i: 'chart-0',   x:  5, y:  0, w: 13, h:  5, minW: 6, minH: 2 },
  { i: 'chart-1',   x: 18, y:  0, w: 12, h:  5, minW: 6, minH: 2 },
  { i: 'trading',   x: 30, y:  0, w:  6, h: 10, minW: 3, maxW: 15, minH: 2 },
  { i: 'chart-2',   x:  5, y:  5, w: 13, h:  5, minW: 6, minH: 2 },
  { i: 'chart-3',   x: 18, y:  5, w: 12, h:  5, minW: 6, minH: 2 },
  // ── bottom row ───────────────────────────────────────────────────────────
  { i: 'signals',   x:  5, y: 10, w:  6, h:  4, minW: 3, minH: 2 },
  { i: 'autotrade', x: 11, y: 10, w: 13, h:  4, minW: 6, minH: 2 },
  { i: 'news',      x: 24, y: 10, w:  6, h:  4, minW: 3, minH: 2 },
  { i: 'reactor',   x: 30, y: 10, w:  6, h:  4, minW: 3, minH: 2 },
];

/** 6 charts in a 3×2 grid + bottom panels row */
export const LAYOUT_6: LayoutItem[] = [
  // ── main area ────────────────────────────────────────────────────────────
  { i: 'sidebar',   x:  0, y:  0, w:  5, h: 14, minW: 3, maxW: 15, minH: 2 },
  { i: 'chart-0',   x:  5, y:  0, w:  8, h:  5, minW: 5, minH: 2 },
  { i: 'chart-1',   x: 13, y:  0, w:  9, h:  5, minW: 5, minH: 2 },
  { i: 'chart-2',   x: 22, y:  0, w:  8, h:  5, minW: 5, minH: 2 },
  { i: 'trading',   x: 30, y:  0, w:  6, h: 10, minW: 3, maxW: 15, minH: 2 },
  { i: 'chart-3',   x:  5, y:  5, w:  8, h:  5, minW: 5, minH: 2 },
  { i: 'chart-4',   x: 13, y:  5, w:  9, h:  5, minW: 5, minH: 2 },
  { i: 'chart-5',   x: 22, y:  5, w:  8, h:  5, minW: 5, minH: 2 },
  // ── bottom row ───────────────────────────────────────────────────────────
  { i: 'signals',   x:  5, y: 10, w:  6, h:  4, minW: 3, minH: 2 },
  { i: 'autotrade', x: 11, y: 10, w: 13, h:  4, minW: 6, minH: 2 },
  { i: 'news',      x: 24, y: 10, w:  6, h:  4, minW: 3, minH: 2 },
  { i: 'reactor',   x: 30, y: 10, w:  6, h:  4, minW: 3, minH: 2 },
];

// ─── PanelShell — HUD chrome around every panel ───────────────────────────────

interface PanelShellProps {
  id:         string;
  title:      string;
  badge?:     React.ReactNode;
  children:   React.ReactNode;
  onClose:    (() => void) | undefined;
  maximised:  boolean;
  onMaximise: () => void;
  /** Chart panels: highlighted as the target of watchlist clicks. */
  focused?:   boolean;
  onFocus?:   (() => void) | undefined;
}

function PanelShell({ id: _id, title, badge, children, onClose, maximised, onMaximise, focused, onFocus }: PanelShellProps) {
  return (
    <div
      className="panel h-full hud-corners hud-corners-full flex flex-col group"
      onPointerDownCapture={onFocus}
      data-focused={focused || undefined}
      style={focused ? { borderColor: 'var(--color-arc)', boxShadow: 'inset 0 0 10px var(--color-glow-str)' } : undefined}
    >
      <div className="holo-grid" />
      <div className="scanline" />

      {/* Header (drag handle) */}
      <div className="panel-header panel-drag-handle flex-shrink-0 select-none">
        <span className="font-orb text-[11px] font-bold tracking-[2px] flex items-center gap-1.5" style={{ color: 'var(--color-arc)', textShadow: '0 0 8px var(--color-arc)' }}>
          <span style={{ width: '2px', height: '10px', background: 'var(--color-arc)', boxShadow: '0 0 4px var(--color-arc)', display: 'inline-block', flexShrink: 0 }} />
          {title}
        </span>
        {badge}
        <div className="ml-auto flex items-center gap-1.5">
          <button
            onMouseDown={e => e.stopPropagation()}
            onClick={onMaximise}
            title={maximised ? 'Restore' : 'Maximise'}
            aria-label={`${maximised ? 'Restore' : 'Maximise'} ${title} panel`}
            className="w-5 h-5 flex items-center justify-center text-[11px] text-muted hover:text-arc transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
          >
            {maximised ? '⊡' : '⊞'}
          </button>
          {onClose && (
            <button
              onMouseDown={e => e.stopPropagation()}
              onClick={onClose}
              title="Remove panel"
              aria-label={`Remove ${title} panel`}
              className="w-5 h-5 flex items-center justify-center text-[11px] text-muted hover:text-sell transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 relative z-[1]">{children}</div>
    </div>
  );
}

// ─── Chart panel inner content ────────────────────────────────────────────────

function ChartPanelItem({ panelIndex }: { panelIndex: number }) {
  const panels         = useTerminalStore(s => s.activePanels);
  const tokens         = useTerminalStore(s => s.tokens);
  const prices         = useTerminalStore(s => s.prices);
  const wsConnected    = useTerminalStore(s => s.wsConnected);
  const setPanelSymbol = useTerminalStore(s => s.setPanelSymbol);
  const setPanelTF     = useTerminalStore(s => s.setPanelTimeframe);

  const panel = panels[panelIndex];
  if (!panel) {
    return (
      <div className="flex items-center justify-center h-full text-muted font-mono text-[11px] opacity-90">
        NO PANEL DATA
      </div>
    );
  }

  return <ChartPanelBody panel={panel} prices={prices} tokens={tokens} wsConnected={wsConnected} setPanelSymbol={setPanelSymbol} setPanelTF={setPanelTF} />;
}

type ChartPanelBodyProps = {
  panel:          ReturnType<typeof useTerminalStore.getState>['activePanels'][number];
  prices:         ReturnType<typeof useTerminalStore.getState>['prices'];
  tokens:         ReturnType<typeof useTerminalStore.getState>['tokens'];
  wsConnected:    boolean;
  setPanelSymbol: (id: string, symbol: string) => void;
  setPanelTF:     ReturnType<typeof useTerminalStore.getState>['setPanelTimeframe'];
};

function ChartPanelBody({ panel, prices, tokens, wsConnected, setPanelSymbol, setPanelTF }: ChartPanelBodyProps) {
  // Same source as the chart's price axis (last candle close), same formatter as the top bar.
  const displayPrice = useTerminalStore(selectDisplayPrice(panel.symbol, panel.timeframe));
  const feedStatus   = useTerminalStore(selectFeedStatus(panel.symbol, panel.timeframe));
  const retryFeeds   = useTerminalStore(s => s.retryFeeds);
  const setPanelConfig = useTerminalStore(s => s.setPanelConfig);
  const marketCap    = useTerminalStore(s => s.marketCaps[panel.symbol]);
  const change       = prices[panel.symbol]?.change24h;
  const hasData      = displayPrice > 0;
  const priceStr     = hasData ? formatPriceWithSymbol(displayPrice) : '---';
  const priceColor   = !hasData || change === undefined || change === 0
    ? 'var(--color-text)'
    : change > 0 ? 'var(--color-buy)' : 'var(--color-sell)';

  const feed = feedStatus === 'loading'
    ? { label: 'LOADING', cls: 'badge-signal', tip: 'Fetching candle history' }
    : feedStatus !== 'ok'
      ? { label: 'NO DATA', cls: 'badge-sell', tip: 'No candle data from any source — press RETRY' }
      : wsConnected
        ? { label: 'LIVE', cls: 'badge-live', tip: 'Streaming via WebSocket' }
        : { label: 'DELAYED', cls: 'badge-signal', tip: 'WebSocket disconnected — showing last loaded candles, reconnecting' };

  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1 bg-black/20 flex-shrink-0 border-b border-hud">
        <select
          value={panel.symbol}
          onChange={e => setPanelSymbol(panel.id, e.target.value)}
          onMouseDown={e => e.stopPropagation()}
          aria-label="Chart symbol"
          className="bg-transparent text-arc font-orb font-bold text-[11px] cursor-pointer border-0 tracking-wider"
        >
          {tokens.map(t => (
            <option key={t.symbol} value={t.symbol} className="bg-[#040d1a] text-bright">{t.symbol}</option>
          ))}
        </select>
        <span className="text-muted text-[11px]">/ USDT</span>
        <span className={`badge ${feed.cls} ml-1`} title={feed.tip} role="status">{feed.label}</span>
        {feedStatus !== 'ok' && feedStatus !== 'loading' && (
          <button
            onMouseDown={e => e.stopPropagation()}
            onClick={retryFeeds}
            aria-label={`Retry loading ${panel.symbol} data`}
            title="Retry loading candle data"
            className="font-mono text-[11px] border border-arc/60 text-arc px-1.5 leading-4 hover:bg-arc/10 tracking-widest"
          >
            ↻ RETRY
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={panel.timeframe}
            onChange={e => setPanelTF(panel.id, e.target.value as typeof panel.timeframe)}
            onMouseDown={e => e.stopPropagation()}
            aria-label="Chart timeframe"
            className="bg-transparent text-accent font-mono text-[11px] cursor-pointer border border-accent/30 px-1"
          >
            {['1m','5m','15m','30m','1h','4h','1D','1W'].map(tf => (
              <option key={tf} value={tf} className="bg-[#040d1a]">{tf}</option>
            ))}
          </select>
          {/* This chart's own UT Bot settings: its arrows and trailing stop only.
              The bot's settings (signals, auto-trade) are in the signal panel. */}
          <span className="flex items-center gap-0.5 font-mono text-[11px] text-muted" title="This chart's UT Bot settings (arrows only). The bot's settings are in UT BOT SIGNALS → BOT CONFIG.">
            K
            <input
              type="number" min={0.1} max={5} step={0.1}
              value={panel.config.keyValue}
              onChange={e => setPanelConfig(panel.id, { keyValue: parseFloat(e.target.value) })}
              onMouseDown={e => e.stopPropagation()}
              aria-label="Chart UT Bot key value"
              className="w-10 bg-transparent text-accent border border-accent/30 px-0.5"
            />
            P
            <input
              type="number" min={2} max={50} step={1}
              value={panel.config.atrPeriod}
              onChange={e => setPanelConfig(panel.id, { atrPeriod: parseInt(e.target.value) })}
              onMouseDown={e => e.stopPropagation()}
              aria-label="Chart UT Bot ATR period"
              className="w-9 bg-transparent text-accent border border-accent/30 px-0.5"
            />
          </span>
          <span className="font-orb text-[11px] font-bold" style={{ color: priceColor }}>
            {priceStr}
          </span>
          {marketCap !== undefined && (
            <span className="font-mono text-[11px] text-muted cursor-help" title="Market cap: price × circulating supply (CoinGecko, refreshed about every 15s)">
              MCAP <span className="text-bright">{formatMarketCap(marketCap)}</span>
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <ChartPanel symbol={panel.symbol} timeframe={panel.timeframe} config={panel.config} className="h-full" />
      </div>
    </div>
  );
}

// ─── DynamicGrid ──────────────────────────────────────────────────────────────

const COLS = 36;
const DEFAULT_ROWS = 10; // Fixed row count used for rowHeight — never changes during drag/resize

export const DynamicGrid = memo(function DynamicGrid() {
  const storedLayouts   = useTerminalStore(s => s.panelLayouts);
  const setPanelLayouts = useTerminalStore(s => s.setPanelLayouts);
  const addPanelSymbol  = useTerminalStore(s => s.addPanelSymbol);
  const activePanels    = useTerminalStore(s => s.activePanels);
  const uiScale         = useTerminalStore(s => s.uiScale);

  // When the page has CSS zoom applied (document.documentElement.style.zoom),
  // getBoundingClientRect() values are in zoomed viewport pixels while
  // offsetParent.scrollTop is in CSS (unzoomed) pixels. RGL's default
  // transformScale=1 mixes these two spaces → the dragged panel jumps on
  // mousedown, proportional to how far the grid is scrolled.
  // Passing scale=uiScale tells RGL to divide BCR values by zoom before
  // adding the CSS-pixel scroll offset, keeping everything in one space.
  const positionStrategy = useMemo(() => ({
    type:      'transform' as const,
    scale:     uiScale,
    calcStyle: setTransform,
  }), [uiScale]);

  const [maximisedId, setMaximisedId] = useState<string | null>(null);
  const hiddenList  = useTerminalStore(s => s.hiddenPanelIds);
  const hidePanel   = useTerminalStore(s => s.hidePanel);
  const showPanel   = useTerminalStore(s => s.showPanel);
  const hiddenIds   = new Set(hiddenList);
  const focusedIdx  = useTerminalStore(s => s.focusedPanelIndex);
  const focusPanel  = useTerminalStore(s => s.focusPanel);
  const [gridWidth,   setGridWidth]   = useState(window.innerWidth);
  const [rowHeight,   setRowHeight]   = useState(58);
  // gridKey: changing this forces GridLayout to remount (e.g. on RESET).
  // RGL treats its layout prop as initial state, so external resets require remount.
  const [gridKey, setGridKey]         = useState(0);
  const containerRef      = useRef<HTMLDivElement>(null);
  const layoutsRef        = useRef<LayoutItem[]>([]);
  const preDragRef        = useRef<LayoutItem[] | null>(null);
  // true while a drag gesture is in progress — onLayoutChange saves to store ONLY
  // during drag so the store tracks RGL's live position, making snap-back a real change.
  const dragInProgressRef = useRef(false);
  const resizeHappenedRef = useRef(false);

  const layouts = (storedLayouts.length > 0 ? storedLayouts : DEFAULT_LAYOUT) as LayoutItem[];
  layoutsRef.current = layouts;

  // Detect RESET: storedLayouts transitions from non-empty → empty.
  // Since RGL treats its layout prop as initial/internal state and won't re-sync
  // from an external prop change alone, we increment gridKey to force a remount.
  const prevStoredLenRef = useRef(storedLayouts.length);
  useLayoutEffect(() => {
    const prev = prevStoredLenRef.current;
    prevStoredLenRef.current = storedLayouts.length;
    if (prev > 0 && storedLayouts.length === 0) {
      setGridKey(k => k + 1);
    }
  }, [storedLayouts.length]);

  const recalcRowHeight = useCallback((containerH: number) => {
    const margin  = 2;
    const padding = 2;
    const rh = Math.floor((containerH - margin * (DEFAULT_ROWS - 1) - padding * 2) / DEFAULT_ROWS);
    setRowHeight(Math.max(rh, 40));
  }, []);

  // Re-measure the grid container. Runs on element resize, window resize
  // (browser zoom in/out/reset fires `resize` but not always a ResizeObserver
  // callback) and after uiScale changes, so the grid realigns to its container.
  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (el.clientWidth)  setGridWidth(el.clientWidth);
    if (el.clientHeight) recalcRowHeight(el.clientHeight);
  }, [recalcRowHeight]);

  useEffect(() => {
    let raf = 0;
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    const ro = new ResizeObserver(schedule);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', schedule);
      cancelAnimationFrame(raf);
    };
  }, [measure, uiScale, maximisedId]);

  // ── Lower-panel scroll hint ────────────────────────────────────────────────
  const [moreBelow, setMoreBelow] = useState(false);
  const updateMoreBelow = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 24);
  }, []);
  useEffect(() => { updateMoreBelow(); });

  // noCompactor keeps other panels from moving during drag — no static freeze needed.
  const visible = layouts.filter(l => !hiddenIds.has(l.i));

  // Watchlist clicks load into the focused chart, so keep focus on a chart the
  // user can see: if it was removed or isn't in the layout, move it to the
  // first visible one.
  const visibleCharts = visible
    .filter(l => l.i.startsWith('chart-'))
    .map(l => parseInt(l.i.slice(6), 10))
    .filter(i => i < activePanels.length)
    .sort((a, b) => a - b);
  const firstVisibleChart = visibleCharts[0];
  const focusIsVisible    = visibleCharts.includes(focusedIdx);
  useEffect(() => {
    if (!focusIsVisible && firstVisibleChart !== undefined) focusPanel(firstVisibleChart);
  }, [focusIsVisible, firstVisibleChart, focusPanel]);
  const chartFocus: ChartFocus = { index: focusedIdx, show: visibleCharts.length > 1, set: focusPanel };

  // ── Drag = swap, not free move ─────────────────────────────────────────────
  // RGL v2 EventCallback: (layout, oldItem, newItem, placeholder, event, element)

  const handleDragStart = useCallback(() => {
    preDragRef.current        = layoutsRef.current.map(l => ({ ...l }));
    dragInProgressRef.current = true;
  }, []);

  const handleDragStop = useCallback((
    _layout:      readonly LayoutItem[],
    oldItem:      LayoutItem | null,
    _newItem:     LayoutItem | null,
    _placeholder: LayoutItem | null,
    event:        Event,
  ) => {
    dragInProgressRef.current = false; // clear BEFORE setPanelLayouts so onLayoutChange won't re-save
    const pre      = preDragRef.current ?? layoutsRef.current;
    const draggedId = oldItem?.i ?? null;
    preDragRef.current = null;

    if (!draggedId) { setPanelLayouts([...pre]); return; }

    const e  = event as MouseEvent;
    const mx = e.clientX;
    const my = e.clientY;
    let targetId: string | null = null;

    const container = containerRef.current;
    if (container) {
      const cRect = container.getBoundingClientRect();
      const cW    = cRect.width  || 1;
      const cH    = cRect.height || 1;
      const normX = (mx - cRect.left) / cW;
      const normY = (my - cRect.top)  / cH;
      const panelDivs = container.querySelectorAll<HTMLElement>('[data-panel-id]');
      for (const div of panelDivs) {
        const pid = div.getAttribute('data-panel-id');
        if (!pid || pid === draggedId) continue;
        const r  = div.getBoundingClientRect();
        const l  = (r.left   - cRect.left) / cW;
        const ri = (r.right  - cRect.left) / cW;
        const t  = (r.top    - cRect.top)  / cH;
        const b  = (r.bottom - cRect.top)  / cH;
        if (normX >= l && normX <= ri && normY >= t && normY <= b) {
          targetId = pid;
          break;
        }
      }
    }

    const src    = pre.find(l => l.i === draggedId) ?? null;
    const target = targetId ? pre.find(l => l.i === targetId) ?? null : null;

    if (src && target) {
      setPanelLayouts(pre.map(item => {
        if (item.i === draggedId) return { ...item, x: target.x, y: target.y, w: target.w, h: target.h };
        if (item.i === target.i)  return { ...item, x: src.x,    y: src.y,    w: src.w,    h: src.h    };
        return item;
      }));
    } else {
      // Snap-back to pre-drag position.
      // onLayoutChange tracks every RGL move to the store, so the store currently holds
      // the drifted position → setPanelLayouts(pre) IS a real Zustand change → RGL re-syncs.
      setPanelLayouts([...pre]);
    }
  }, [setPanelLayouts]);

  // onLayoutChange: save to store ONLY during an active drag (so the store tracks
  // RGL's live position, making snap-back a real Zustand change) or after a resize.
  // Saving at all other times would create an infinite render loop:
  //   store update → re-render → RGL fires onLayoutChange → store update → …
  // RGL only knows the visible panels, so keep hidden panels' entries when saving
  // its layout; otherwise a resize would delete them and "restore" would do nothing.
  const handleLayoutChange = useCallback((
    newLayout: readonly { i: string; x: number; y: number; w: number; h: number }[],
  ) => {
    if (!dragInProgressRef.current && !resizeHappenedRef.current) return;
    resizeHappenedRef.current = false;
    const shown = new Set(newLayout.map(l => l.i));
    setPanelLayouts([
      ...newLayout,
      ...layoutsRef.current.filter(l => !shown.has(l.i)),
    ] as LayoutItem[]);
  }, [setPanelLayouts]);

  const handleResizeStop = useCallback(() => {
    resizeHappenedRef.current = true;
  }, []);

  const toggleMax  = (id: string) => setMaximisedId(p => p === id ? null : id);

  const addChartPanel = useCallback(() => {
    const syms = ['SOL','BNB','WIF','JTO','ETH','BTC'];
    const next = syms[activePanels.length % syms.length] ?? 'BTC';
    addPanelSymbol(next);
    const newId  = `chart-${activePanels.length}`;
    const maxY   = Math.max(...layouts.map(l => l.y + l.h), 0);
    setPanelLayouts([...layouts, { i: newId, x: 0, y: maxY, w: 10, h: 6, minW: 6, minH: 4 }]);
  }, [activePanels, addPanelSymbol, layouts, setPanelLayouts]);

  const chartLabels = activePanels.map(p => `${p.symbol}/USDT · ${p.timeframe}`);

  // Maximised single-panel view
  if (maximisedId) {
    return (
      <div className="flex-1 relative min-h-0 p-0.5">
        <div className="h-full">
          {renderContent(maximisedId, activePanels.length, () => toggleMax(maximisedId), true, undefined, addChartPanel, chartLabels, chartFocus)}
        </div>
        <button
          onClick={() => setMaximisedId(null)}
          className="absolute top-2 right-2 z-50 font-mono text-[11px] text-muted hover:text-arc border border-hud px-2 py-1 bg-[#040d1a]/80 transition-colors"
        >
          ⊡ RESTORE
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} onScroll={updateMoreBelow} className="flex-1 relative min-h-0 overflow-auto">
      <GridLayout
        key={gridKey}
        layout={visible}
        width={gridWidth}
        gridConfig={{ cols: COLS, rowHeight: rowHeight, margin: [2, 2], containerPadding: [2, 2] }}
        dragConfig={{ handle: '.panel-drag-handle' }}
        resizeConfig={{ handles: ['s', 'e', 'se', 'sw', 'w', 'n', 'ne', 'nw'] }}
        positionStrategy={positionStrategy}
        compactor={preventCollisionCompactor}
        onDragStart={handleDragStart}
        onDragStop={handleDragStop}
        onResizeStop={handleResizeStop}
        onLayoutChange={handleLayoutChange}
        style={{ minHeight: '100%' }}
      >
        {visible.map(item => (
          <div key={item.i} data-panel-id={item.i}>
            {renderContent(
              item.i,
              activePanels.length,
              () => toggleMax(item.i),
              false,
              item.i !== 'sidebar' ? () => hidePanel(item.i) : undefined,
              addChartPanel,
              chartLabels,
              chartFocus,
            )}
          </div>
        ))}
      </GridLayout>

      {/* Bottom action bar: in normal flow (sticky) so it never covers the last
          row of panels (e.g. ARC Reactor) once the user has scrolled down. */}
      <div
        className="sticky bottom-0 left-0 z-40 w-full flex flex-wrap items-center justify-between gap-1 px-2 py-1"
        style={{ background: moreBelow ? 'linear-gradient(to top, rgba(4,13,26,0.97), rgba(4,13,26,0.6))' : 'transparent' }}
      >
        {moreBelow ? (
          <button
            onClick={() => containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight, behavior: 'smooth' })}
            aria-label="Scroll to lower panels"
            className="font-mono text-[11px] tracking-widest text-arc py-0.5 px-1 hover:bg-arc/10 transition-colors text-left min-w-0 break-words"
          >
            ▼ MORE PANELS BELOW — SIGNALS · AUTO-TRADE · INTEL · REACTOR
          </button>
        ) : <span />}
        <div className="flex flex-wrap gap-1 justify-end ml-auto">
          {/* Restore hidden built-in panels */}
          {[...hiddenIds].map(id => (
            <button
              key={id}
              onClick={() => showPanel(id)}
              className="font-mono text-[11px] border border-arc/40 text-arc px-2 py-1 bg-[#040d1a]/80 hover:bg-arc/10 transition-colors tracking-wider"
            >
              + {id.startsWith('chart-')
                  ? (activePanels[parseInt(id.slice(6), 10)]?.symbol ?? id.replace('-', ' ').toUpperCase())
                  : id.replace('-', ' ').toUpperCase()}
            </button>
          ))}

          {/* Add optional panels if not yet in layout */}
          {[
            { id: 'price-alerts', label: '🔔 ALERTS',   w: 3, h: 5, tip: 'Add price alerts panel' },
            { id: 'mtf',          label: '⬡ MTF',       w: 4, h: 5, tip: 'Multi-Timeframe confluence: UT Bot signal agreement across timeframes' },
            { id: 'portfolio',    label: '📊 PORTFOLIO', w: 3, h: 7, tip: 'Add portfolio analytics panel' },
            { id: 'aster',        label: '⚡ ASTER LIVE', w: 9, h: 8, tip: 'Live perpetuals trading on Aster DEX (needs the Aster API set up on the server)' },
          ]
            .filter(p => !layouts.some(l => l.i === p.id) && !hiddenIds.has(p.id))
            .map(p => (
              <button
                key={p.id}
                title={p.tip}
                onClick={() => {
                  const maxY = Math.max(...layouts.map(l => l.y + l.h), 0);
                  setPanelLayouts([...layouts, { i: p.id, x: 0, y: maxY, w: p.w, h: p.h, minW: 4, minH: 3 }]);
                }}
                className="font-mono text-[11px] border border-hud text-bright px-2 py-1 bg-[#040d1a] hover:border-arc hover:text-arc transition-colors tracking-wider"
              >
                + {p.label}
              </button>
            ))
          }
        </div>
      </div>
    </div>
  );
});

// ─── WebSocket status badge (real connection state, not a static label) ───────

function WsBadge() {
  const wsConnected = useTerminalStore(s => s.wsConnected);
  return (
    <span
      role="status"
      title={wsConnected ? 'Signal stream connected' : 'Signal stream disconnected — reconnecting'}
      className={`badge ${wsConnected ? 'badge-live' : 'badge-signal'}`}
      style={{ clipPath: 'polygon(3px 0%, 100% 0%, calc(100% - 3px) 100%, 0% 100%)' }}
    >
      {wsConnected ? 'LIVE' : 'RECONNECTING'}
    </span>
  );
}

// ─── Content factory ──────────────────────────────────────────────────────────

type ChartFocus = {
  index: number;
  /** Only highlight when there's more than one chart to choose between. */
  show:  boolean;
  set:   (index: number) => void;
};

function renderContent(
  id: string,
  chartCount: number,
  onMaximise: () => void,
  maximised: boolean,
  onClose: (() => void) | undefined,
  onAddChart: () => void,
  chartLabels: string[],
  chartFocus: ChartFocus,
): React.ReactNode {
  if (id === 'sidebar') {
    return (
      <PanelShell id={id} title="ASSETS" badge={<SidebarSettingsBadge />} maximised={maximised} onMaximise={onMaximise} onClose={undefined}>
        <Sidebar />
      </PanelShell>
    );
  }

  if (id === 'signals') {
    return (
      <PanelShell id={id} title="UT BOT SIGNALS" badge={<WsBadge />} maximised={maximised} onMaximise={onMaximise} onClose={onClose}>
        <SignalFeed />
      </PanelShell>
    );
  }

  if (id === 'news') {
    return (
      <PanelShell id={id} title="INTEL FEED" badge={<span className="badge badge-live" style={{ clipPath: 'polygon(3px 0%, 100% 0%, calc(100% - 3px) 100%, 0% 100%)' }}>NEWS</span>} maximised={maximised} onMaximise={onMaximise} onClose={onClose}>
        <NewsPanel />
      </PanelShell>
    );
  }

  if (id === 'reactor') {
    return (
      <PanelShell id={id} title="ARC REACTOR" maximised={maximised} onMaximise={onMaximise} onClose={onClose}>
        <ArcReactor />
      </PanelShell>
    );
  }

  if (id === 'autotrade') {
    return (
      <PanelShell
        id={id}
        title="AUTO-TRADE"
        badge={<span className="badge badge-live" style={{ clipPath: 'polygon(3px 0%, 100% 0%, calc(100% - 3px) 100%, 0% 100%)' }}>AI</span>}
        maximised={maximised}
        onMaximise={onMaximise}
        onClose={onClose}
      >
        <AutoTradePanel />
      </PanelShell>
    );
  }

  if (id === 'trading') {
    return (
      <PanelShell
        id={id}
        title="TRADING"
        maximised={maximised}
        onMaximise={onMaximise}
        onClose={onClose}
      >
        <TradingPanel />
      </PanelShell>
    );
  }

  if (id === 'price-alerts') {
    return (
      <PanelShell
        id={id}
        title="PRICE ALERTS"
        badge={<span className="badge" style={{ background: 'rgba(255,215,0,0.15)', color: '#FFD700', borderColor: 'rgba(255,215,0,0.3)', clipPath: 'polygon(3px 0%, 100% 0%, calc(100% - 3px) 100%, 0% 100%)' }}>🔔</span>}
        maximised={maximised}
        onMaximise={onMaximise}
        onClose={onClose}
      >
        <PriceAlertsPanel />
      </PanelShell>
    );
  }

  if (id === 'mtf') {
    return (
      <PanelShell
        id={id}
 title="MTF CONFLUENCE"
        badge={<span title="Multi-Timeframe confluence: UT Bot signal agreement across timeframes" className="badge badge-live" style={{ clipPath: 'polygon(3px 0%, 100% 0%, calc(100% - 3px) 100%, 0% 100%)' }}>MULTI-TF</span>}
        maximised={maximised}
        onMaximise={onMaximise}
        onClose={onClose}
      >
        <MTFConfluencePanel />
      </PanelShell>
    );
  }

  if (id === 'aster') {
    return (
      <PanelShell
        id={id}
        title="ASTER · LIVE"
        badge={<span className="badge badge-sell" style={{ clipPath: 'polygon(3px 0%, 100% 0%, calc(100% - 3px) 100%, 0% 100%)' }}>REAL FUNDS</span>}
        maximised={maximised}
        onMaximise={onMaximise}
        onClose={onClose}
      >
        <AsterPanel />
      </PanelShell>
    );
  }

  if (id === 'portfolio') {
    return (
      <PanelShell
        id={id}
        title="PORTFOLIO"
        badge={<span className="badge badge-signal" style={{ clipPath: 'polygon(3px 0%, 100% 0%, calc(100% - 3px) 100%, 0% 100%)' }}>ANALYTICS</span>}
        maximised={maximised}
        onMaximise={onMaximise}
        onClose={onClose}
      >
        <PortfolioAnalyticsPanel />
      </PanelShell>
    );
  }

  if (id.startsWith('chart-')) {
    const idx      = parseInt(id.replace('chart-', ''), 10);
    const hasPanel = idx < chartCount;

    return (
      <PanelShell
        id={id}
        title={chartLabels[idx] ?? 'EMPTY CHART'}
        maximised={maximised}
        onMaximise={onMaximise}
        onClose={onClose}
        focused={hasPanel && chartFocus.show && chartFocus.index === idx}
        onFocus={hasPanel ? () => chartFocus.set(idx) : undefined}
        badge={!hasPanel ? (
          <button onMouseDown={e => e.stopPropagation()} onClick={onAddChart} className="badge badge-signal cursor-pointer hover:opacity-80">
            + PANEL
          </button>
        ) : undefined}
      >
        {hasPanel ? (
          <ChartPanelItem panelIndex={idx} />
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="text-arc text-[28px] opacity-20">⬡</div>
            <button
              onMouseDown={e => e.stopPropagation()}
              onClick={onAddChart}
              className="font-mono text-[11px] border border-arc/40 text-arc px-3 py-1.5 hover:bg-arc/10 transition-colors tracking-widest"
            >
              + ADD CHART
            </button>
          </div>
        )}
      </PanelShell>
    );
  }

  return null;
}
